import React, { useCallback, useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { any, object, TypeOf, z } from "zod";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { useSelector } from "react-redux";
import CircularProgress from "@mui/material/CircularProgress";
import SaveIcon from "@mui/icons-material/Save";
import { green } from "@mui/material/colors";
import {
  Box,
  Button,
  TextField,
  FormHelperText,
  Checkbox,
  Autocomplete,
  createFilterOptions,
  Divider,
  Chip,
} from "@mui/material";
import Mapbox from "../../components/Locationmap/Mapbox";
import MapContext from "../../components/Locationmap/MapContext";
import { loadSettings } from "../../redux/actions/settings.actions";
import {
  loadUserNameList,
  loadFacitlies,
  loadScheduleList,
  checkWorkLimit,
} from "../../redux/actions/timesheet.actions";
import parse from "autosuggest-highlight/parse";
import match from "autosuggest-highlight/match";
import Grid from "@mui/material/Grid";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment";
import { storeStateType, useAppDispatch } from "../../redux/store";
import { NameList } from "../../redux/reducers/timesheet.reducer";
import { ICompany } from "../../redux/reducers/company.reducer";
import { DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { CLEAR_TIMESHEET_SCHEDULE_LIST } from "../../redux/types/timesheet.types";
import ModalBoxHeader from "../../components/ModalBoxHeader";
import SelectScheduleInput from "./SelectScheduleInput";

dayjs.extend(customParseFormat);

interface Props {
  isDialogOpened: boolean;
  handleCloseDialog: Function;
  action: string;
  titleName: string;
  filterData: any;
}

// const ITEM_HEIGHT = 48;
// const ITEM_PADDING_TOP = 8;
// const MenuProps = {
//   PaperProps: {
//     style: {
//       maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
//       width: 250,
//     },
//   },
// };

// describes the input data
type Category = {
  id: number;
  parent: string;
  facility_name: ICompany["facility_name"];
  childCategories?: Category[];
};
interface Option {
  id: number;
  facility_name: string;
  depth: number;
  parentId: number | null;
  mainParentId: number | null;
  matchTerms: string[];
}

const toOptions = (
  category: Category,
  depth: number = 0,
  parentId: number | null = null,
  mainParentId: number | null = null
): Option[] => {
  const { id, facility_name, childCategories = [] } = category;

  if (depth === 0 && childCategories.length > 0) {
    mainParentId = id;
  }
  const children = childCategories.flatMap((child) => {
    return toOptions(child, depth + 1, id, mainParentId);
  });
  const option = {
    id,
    facility_name,
    depth,
    parentId,
    mainParentId,
    matchTerms: [facility_name].concat(
      children.map((obj) => obj.facility_name)
    ),
  };

  return [option].concat(children);
};

export function TimesheetForm(props: Props) {
  let { handleCloseDialog, titleName } = props;

  const employeeTitle = `employee`;
  const facilityTitle = `facility`;

  const dispatch = useAppDispatch();
  const { employeenames, schedules, facilities, save } = useSelector(
    (state: storeStateType) => state.employeetimesheet
  );

  const scheduleLoading = useSelector(
    (state: storeStateType) => state.employeetimesheet.loading
  );

  const loading = useSelector((state: storeStateType) => state.UI.loading);
  const [open, setOpen] = React.useState<boolean>(false);
  const [scheduleOptionListOpen, setScheduleOptionListOpen] =
    React.useState<boolean>(false);

  const [activeSchedule] = useState<boolean>(true);
  const [, setMinDate] = useState<string | null>(null);
  const [, setMinTime] = useState<string | null>(null);
  const [, setMaxDate] = useState<string | null>(null);
  const [, setMaxTime] = useState<string | null>(null);

  const [options, setOptions] = React.useState<readonly NameList[]>([]);
  const [scheduleOptions, setScheduleOptions] = React.useState<
    readonly NameList[]
  >([]);

  const employeeLoading = open && options.length === 0;

  const [facilityOptions, setFacilityOptions] = React.useState<Option[]>([]);

  const formSchema = object({
    id: any(),
    shift_start_date: z.string().min(1, "Shift Start Date is required."),
    shift_start_time: z.string().min(1, "Start time is required."),

    shift_end_date: z.string().optional(),
    shift_end_time: z.string().optional(),
    user: z.any(),
    facility: z.any(),

    schedule: z.any(),

    shift_comments: z.string(),
    authorized_wardnamnager: z.string(),
    shift_start_lat_lang: z
      .string()
      .nonempty(`${titleName} Lat/Lang coordinats are required`)
      .regex(/^(-?\d+(\.\d+)?)(,\s*(-?\d+(\.\d+)?))$/, "Invalid Lag Lang"),
    //break_time: z.any()
  });

  type FormInputType = TypeOf<typeof formSchema>;
  const {
    register,
    reset,
    setValue,
    control,
    setError,
    getValues,
    watch,
    formState: { errors },
    handleSubmit,
  } = useForm<FormInputType>({ defaultValues: { user: null, facility: null } });

  const [latlng, setLatlng] = useState<null | string>(null);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [handelaction] = useState<string>("");

  //handle modal close
  const handleClose = useCallback(() => {
    handleCloseDialog(false);
    dispatch({ type: CLEAR_TIMESHEET_SCHEDULE_LIST });
    reset();
  }, [reset, handleCloseDialog, dispatch]);

  //handle employees load
  useEffect(() => {
    let active = true;
    if (!employeeLoading) {
      return undefined;
    }
    (async () => {
      if (active) {
        dispatch<any>(loadUserNameList(""));
      }
    })();
    return () => {
      active = false;
    };
  }, [employeeLoading, dispatch]);

  useEffect(() => {
    if (employeenames) {
      setOptions([...employeenames]);
    }
  }, [employeenames]);

  const selectedUser = watch("user");
  const selectedFacility = watch("facility");

  // handle schedules load
  React.useEffect(() => {
    if (selectedFacility && selectedUser) {
      if (selectedFacility?.mainParentId)
        dispatch<any>(
          loadScheduleList(
            selectedUser?.id,
            selectedFacility?.mainParentId,
            selectedFacility?.id
          )
        );
      else {
        dispatch<any>(loadScheduleList(selectedUser?.id, selectedFacility?.id));
      }

      setValue("schedule", null);
    }
  }, [
    scheduleLoading,
    dispatch,
    watch,
    selectedUser,
    selectedFacility,
    setValue,
  ]);

  React.useEffect(() => {
    if (schedules) {
      setScheduleOptions([...schedules]);
    }
  }, [schedules]);

  React.useEffect(() => {
    dispatch<any>(loadFacitlies());
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(function (position) {
        let coordinte =
          position.coords.latitude + "," + position.coords.longitude;
        reset({ ...getValues(), shift_start_lat_lang: coordinte });
      });
    }
  }, [dispatch, reset, getValues]);

  React.useEffect(() => {
    if (facilities)
      setFacilityOptions(
        facilities.flatMap((category: any) => {
          return toOptions(category);
        })
      );
  }, [facilities]);

  useEffect(() => {
    dispatch<any>(loadSettings());
  }, [dispatch]);

  useEffect(() => {
    if (save) {
      handleClose();
    }
  }, [save, dispatch, handleClose]);

  useEffect(() => {
    if (latlng) {
      reset({ ...getValues(), shift_start_lat_lang: latlng });
    }
  }, [latlng, reset, getValues]);

  const handleClick: React.MouseEventHandler<HTMLElement> = () => {
    setIsOpen(!isOpen);
  };

  const onFormSubmit: SubmitHandler<FormInputType> = (data) => {
    let error = false;

    const parseResult: any = formSchema.safeParse(data);
    if (!parseResult.success) {
      let errorKeys: any = Object.keys(
        parseResult?.error?.formErrors?.fieldErrors
      );
      let errorValues: any = Object.values(
        parseResult?.error?.formErrors?.fieldErrors
      );

      for (let i = 0; i < errorKeys.length; i++) {
        setError(errorKeys[i], { type: "custom", message: errorValues[i][0] });
        error = true;
      }
    }
    if (data.user == null) {
      setError("user", {
        type: "custom",
        message: `${employeeTitle} Name is required.`,
      });
      error = true;
    }
    if (data.facility == null) {
      setError("facility", {
        type: "custom",
        message: `${facilityTitle} Name is required.`,
      });
      error = true;
    }
    //checking timesheet end date and time
    // if (!data.shift_end_date) {
    //   setError("shift_end_date", {
    //     type: "custom",
    //     message: "End date is required",
    //   });
    //   error = true;
    // }
    // if (!data.shift_end_time) {
    //   setError("shift_end_time", {
    //     type: "custom",
    //     message: "Required",
    //   });
    //   error = true;
    // }
    if (data.schedule) {
      //scope for checking schedule end time is before selected end date and time
      let scheduleObj = watch("schedule");

      if (data.shift_end_time && data.shift_end_date) {
        let startTimeObj = moment(data.shift_start_date).add(
          data.shift_start_time
        );
        let endTimeObj = moment(data.shift_end_date).add(data.shift_end_time);
        let scheduleStartTimeObj = moment(scheduleObj?.start_time);
        let scheduleEndTimeObj = moment(scheduleObj?.end_time);
        if (scheduleEndTimeObj.isBefore(endTimeObj)) {
          setError("shift_end_time", {
            type: "custom",
            message: "Shift end time is after schedule end time",
          });
          error = true;
        }
        if (startTimeObj.isBefore(scheduleStartTimeObj)) {
          setError("shift_start_time", {
            type: "custom",
            message: "Shift start time is before schedule start time",
          });
          error = true;
        }
      }
    }

    if (
      data.shift_end_date &&
      data.shift_start_date &&
      data.shift_end_time &&
      data.shift_start_time
    ) {
      let startTimeObj = moment(data.shift_start_date).add(
        data.shift_start_time
      );
      let endTimeObj = moment(data.shift_end_date).add(data.shift_end_time);

      if (startTimeObj.isAfter(endTimeObj)) {
        setError("shift_end_date", {
          type: "custom",
          message: "End date is before start date.",
        });
        error = true;
      } else if (startTimeObj.isSame(endTimeObj)) {
        setError("shift_end_time", {
          type: "custom",
          message: "Shift start, end time is same",
        });
        error = true;
      }
    }
    if (error) return false;

    const formData = new FormData();
    formData.append("facility", data.facility.id);
    formData.append("top_parent_facility", data.facility.mainParentId ?? "");
    formData.append("shift_start_date", data.shift_start_date);
    formData.append("shift_start_time", data.shift_start_time);
    if (data?.shift_end_time) {
      formData.append("shift_end_date", data.shift_end_date ?? "");
      formData.append("shift_end_time", data.shift_end_time ?? "");
    }
    formData.append("user", data.user.id);
    formData.append("shift_start_lat_lang", data.shift_start_lat_lang);
    formData.append("shift_comments", data.shift_comments);
    formData.append("authorized_wardnamnager", data.authorized_wardnamnager);
    if (data?.schedule?.id) formData.append("schedule_id", data?.schedule?.id);
    //formData.append('break_time', data.break_time)
    dispatch(checkWorkLimit(data.user.id, formData, props.filterData));
    // dispatch<any>(saveTimesheet(formData, props.filterData));
  };
  const handleMapBoxClose = useCallback(() => {
    setIsOpen(false);
  }, []);
  return (
    <MapContext.Provider value={{ latlng, setLatlng }}>
      <Dialog open={props.isDialogOpened} fullWidth>
        <ModalBoxHeader title={`New ${titleName}`} handleClose={handleClose} />
        <DialogContent>
          <Box
            component="form"
            id="customfieldform"
            name="customfieldform"
            noValidate
            onSubmit={handleSubmit(onFormSubmit)}
          >
            <Controller
              control={control}
              name="user"
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  fullWidth
                  id="user"
                  open={open}
                  onOpen={() => {
                    setOpen(true);
                  }}
                  onClose={() => {
                    setOpen(false);
                  }}
                  onChange={(event, item) => {
                    onChange(item);
                  }}
                  value={value}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  getOptionLabel={(option) =>
                    option.fullname ? `${option.fullname}(${option.id})` : ""
                  }
                  options={options}
                  loading={loading}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={`Employee name`}
                      variant="standard"
                      error={!!errors["user"]}
                      helperText={errors["user"] ? errors["user"].message : ""}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <React.Fragment>
                            {loading ? (
                              <CircularProgress color="inherit" size={20} />
                            ) : null}
                            {params.InputProps.endAdornment}
                          </React.Fragment>
                        ),
                      }}
                    />
                  )}
                />
              )}
            />

            <Controller
              control={control}
              name="facility"
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  onChange={(event, item) => {
                    if (item) {
                      if (item?.depth === 0) {
                        item.mainParentId = null;
                      }
                      onChange(item);
                    }
                  }}
                  value={value}
                  options={facilityOptions}
                  getOptionLabel={(option) =>
                    option.facility_name ? option.facility_name : ""
                  }
                  renderOption={(props, option, { selected, inputValue }) => {
                    const matches = match(option.facility_name, inputValue);
                    const parts = parse(option.facility_name, matches);
                    return (
                      <li {...props}>
                        <Checkbox
                          checked={selected}
                          sx={{ ml: 2 * option.depth }}
                        />
                        <div>
                          {parts.map((part: any, index: number) => (
                            <span
                              key={index}
                              style={{
                                fontWeight: part.highlight ? 700 : 400,
                              }}
                            >
                              {part.text}
                            </span>
                          ))}
                        </div>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label={`Facility name`}
                      error={!!errors["facility"]}
                      helperText={
                        errors["facility"] ? errors["facility"].message : ""
                      }
                    />
                  )}
                  filterOptions={createFilterOptions({
                    // join with some arbitrary separator to prevent matches across adjacent terms
                    stringify: (option: any) => option.matchTerms.join("//"),
                  })}
                />
              )}
            />
            {activeSchedule && (
              <SelectScheduleInput
                errors={errors}
                control={control}
                scheduleOptionListOpen={scheduleOptionListOpen}
                setScheduleOptionListOpen={setScheduleOptionListOpen}
                setMinDate={setMinDate}
                setMaxDate={setMaxDate}
                setMinTime={setMinTime}
                setMaxTime={setMaxTime}
                setValue={setValue}
                scheduleOptions={scheduleOptions}
                scheduleLoading={scheduleLoading}
                activeSchedule={false} //this is for required schedule
              />
            )}

            <TextField
              margin="dense"
              id="shift_comments"
              label="Comment"
              fullWidth
              variant="standard"
              {...register("shift_comments")}
              error={!!errors["shift_comments"]}
              helperText={
                errors["shift_comments"] ? errors["shift_comments"].message : ""
              }
            />

            <TextField
              margin="dense"
              id="authorized_wardnamnager"
              label="Authorized Ward Manager"
              fullWidth
              variant="standard"
              {...register("authorized_wardnamnager")}
              error={!!errors["authorized_wardnamnager"]}
              helperText={
                errors["authorized_wardnamnager"]
                  ? errors["authorized_wardnamnager"].message
                  : ""
              }
            />

            <Grid container spacing={0} sx={{ marginTop: "10px" }}>
              <Grid item xs={6}>
                <Divider>
                  <Chip label="ClockIn" />
                </Divider>
                <Controller
                  control={control}
                  name="shift_start_date"
                  render={({ field: { onChange, value } }) => (
                    <>
                      <DatePicker
                        onChange={(valueObj) => {
                          onChange(valueObj?.format("YYYY-MM-DD"));
                        }}
                        value={value ? moment(value) : null}
                        maxDate={moment()}
                        format="YYYY-MM-DD"
                      />
                      <FormHelperText error={!!errors["shift_start_date"]}>
                        {errors["shift_start_date"]
                          ? errors["shift_start_date"].message
                          : ""}
                      </FormHelperText>
                    </>
                  )}
                />
                <Controller
                  control={control}
                  name="shift_start_time"
                  //rules={{ required: true ,}}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <TimePicker
                        label="Start Time"
                        sx={{ my: 0 }}
                        value={value ? moment(value, "HH:mm:ss") : null}
                        onChange={(selectedDateObj: moment.Moment | null) => {
                          onChange(
                            selectedDateObj
                              ?.set("seconds", 0)
                              ?.format("HH:mm:ss")
                          );
                        }}
                        format="HH:mm"
                        ampm={false}
                      />

                      <FormHelperText error={!!errors["shift_start_time"]}>
                        {errors["shift_start_time"]
                          ? errors["shift_start_time"].message
                          : ""}
                      </FormHelperText>
                    </>
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <Divider>
                  <Chip label="ClockOut" />
                </Divider>

                <Controller
                  control={control}
                  name="shift_end_date"
                  render={({ field: { onChange, value } }) => (
                    <>
                      <DatePicker
                        onChange={(valueObj) => {
                          onChange(valueObj?.format("YYYY-MM-DD"));
                        }}
                        value={value ? moment(value) : null}
                        format="YYYY-MM-DD"
                        maxDate={moment()}
                      />
                      <FormHelperText error={!!errors["shift_end_date"]}>
                        {errors["shift_end_date"]
                          ? errors["shift_end_date"].message
                          : ""}
                      </FormHelperText>
                    </>
                  )}
                />

                <Controller
                  control={control}
                  name="shift_end_time"
                  render={({ field: { onChange, value } }) => (
                    <>
                      <TimePicker
                        label="End Time"
                        sx={{ my: 1 }}
                        value={value ? moment(value, "HH:mm:ss") : null}
                        onChange={(selectedDateObj: moment.Moment | null) => {
                          onChange(
                            selectedDateObj
                              ?.set("seconds", 0)
                              ?.format("HH:mm:ss")
                          );
                        }}
                        format="HH:mm"
                        ampm={false}
                      />

                      <FormHelperText error={!!errors["shift_end_time"]}>
                        {errors["shift_end_time"]
                          ? errors["shift_end_time"].message
                          : ""}
                      </FormHelperText>
                    </>
                  )}
                />
              </Grid>
            </Grid>
            <Grid container>
              <Grid item xs={12}>
                <TextField
                  margin="dense"
                  id="name"
                  label="Clockin Lat/Lng"
                  fullWidth
                  variant="standard"
                  required
                  {...register("shift_start_lat_lang")}
                  error={!!errors["shift_start_lat_lang"]}
                  helperText={
                    errors["shift_start_lat_lang"]
                      ? errors["shift_start_lat_lang"].message
                      : ""
                  }
                  onClick={handleClick}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </Grid>
            </Grid>

            <DialogActions>
              <Box sx={{ m: 1, position: "relative" }}>
                <Button
                  type="submit"
                  form="customfieldform"
                  variant="contained"
                  color="success"
                  startIcon={<SaveIcon />}
                  disabled={loading}
                >
                  Save
                </Button>
                {loading && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: green[500],
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                )}
              </Box>
              <Button
                onClick={handleClose}
                variant="contained"
                color="error" /* disabled={loading} */
              >
                Cancel
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>
      <Mapbox
        isDialogOpened={isOpen}
        handleCloseDialog={handleMapBoxClose}
        action={handelaction}
      />
    </MapContext.Provider>
  );
}
